interface BrowserVerificationResult {
  isVerified: boolean;
  score: number;
  flags: string[];
}

interface MouseMovementData {
  movements: number;
  distance: number;
  startTime: number;
}

const VERIFICATION_KEY = 'browser_verification_token';
const VERIFICATION_EXPIRY = 30 * 60 * 1000;

let mouseData: MouseMovementData = {
  movements: 0,
  distance: 0,
  startTime: Date.now(),
};

let lastMousePosition = { x: 0, y: 0 };
let isTracking = false;

export function startMouseTracking(): void {
  if (isTracking) return;

  isTracking = true;
  mouseData = {
    movements: 0,
    distance: 0,
    startTime: Date.now(),
  };

  const handleMouseMove = (e: MouseEvent) => {
    const dx = e.clientX - lastMousePosition.x;
    const dy = e.clientY - lastMousePosition.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    mouseData.movements++;
    mouseData.distance += distance;

    lastMousePosition = { x: e.clientX, y: e.clientY };
  };

  document.addEventListener('mousemove', handleMouseMove);

  setTimeout(() => {
    document.removeEventListener('mousemove', handleMouseMove);
    isTracking = false;
  }, 60000);
}

export function verifyBrowser(): BrowserVerificationResult {
  const flags: string[] = [];
  let score = 100;

  if (typeof window === 'undefined') {
    return { isVerified: false, score: 0, flags: ['no_window'] };
  }

  const nav = window.navigator;

  if (!nav.userAgent || nav.userAgent.length < 10) {
    flags.push('missing_user_agent');
    score -= 30;
  }

  if ((nav as Navigator & { webdriver?: boolean }).webdriver) {
    flags.push('webdriver_detected');
    score -= 50;
  }

  if (!nav.language && !nav.languages) {
    flags.push('no_language');
    score -= 15;
  }

  if (!nav.cookieEnabled) {
    flags.push('cookies_disabled');
    score -= 10;
  }

  const screen = window.screen;
  if (!screen || screen.width === 0 || screen.height === 0) {
    flags.push('invalid_screen');
    score -= 20;
  }

  if (screen && (screen.width < 100 || screen.height < 100)) {
    flags.push('suspicious_screen_size');
    score -= 15;
  }

  if (!window.localStorage || !window.sessionStorage) {
    flags.push('no_storage');
    score -= 10;
  }

  const timeSpent = Date.now() - mouseData.startTime;
  if (timeSpent > 2000 && mouseData.movements < 3) {
    flags.push('no_mouse_movement');
    score -= 20;
  }

  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      flags.push('no_canvas_support');
      score -= 10;
    }
  } catch {
    flags.push('canvas_error');
    score -= 10;
  }

  if ('chrome' in window && !('app' in (window as Window & { chrome?: { app?: unknown } }).chrome!)) {
    flags.push('suspicious_chrome');
    score -= 5;
  }

  const isVerified = score >= 50;

  if (isVerified) {
    storeVerificationToken();
  }

  return {
    isVerified,
    score: Math.max(0, score),
    flags,
  };
}

export function generateVerificationToken(): string {
  const data = {
    timestamp: Date.now(),
    userAgent: navigator.userAgent,
    screenSize: `${screen.width}x${screen.height}`,
    random: Math.random().toString(36).substring(2),
  };

  return btoa(JSON.stringify(data));
}

function storeVerificationToken(): void {
  try {
    const token = {
      value: generateVerificationToken(),
      expires: Date.now() + VERIFICATION_EXPIRY,
    };
    sessionStorage.setItem(VERIFICATION_KEY, JSON.stringify(token));
  } catch {
    // Storage might be disabled
  }
}

export function hasValidVerification(): boolean {
  try {
    const stored = sessionStorage.getItem(VERIFICATION_KEY);
    if (!stored) return false;

    const token = JSON.parse(stored);
    if (token.expires < Date.now()) {
      sessionStorage.removeItem(VERIFICATION_KEY);
      return false;
    }

    return true;
  } catch {
    return false;
  }
}

export function clearVerification(): void {
  try {
    sessionStorage.removeItem(VERIFICATION_KEY);
  } catch {
    // Storage might be disabled
  }
}

export function getMouseActivityScore(): number {
  const timeSpent = (Date.now() - mouseData.startTime) / 1000;

  if (timeSpent < 1) return 0;

  const movementsPerSecond = mouseData.movements / timeSpent;
  const avgDistancePerMove =
    mouseData.movements > 0 ? mouseData.distance / mouseData.movements : 0;

  let score = 0;

  if (movementsPerSecond > 0.5 && movementsPerSecond < 50) {
    score += 40;
  }

  if (avgDistancePerMove > 5 && avgDistancePerMove < 500) {
    score += 30;
  }

  if (mouseData.movements > 5) {
    score += 30;
  }

  return Math.min(score, 100);
}
